import React, { useState, useEffect, useRef } from "react";
import { User } from "../../types/interfaces";
import useAxios from "../../hooks/useAxios";

interface PanditProps {
  show_label: boolean;
  label?: string;
  placeholder?: string;
  selectedValue?: User;
  onChange: (option: User | null) => void;
  is_required?: boolean;
  error?: string;
}

const CommonPanditSelect: React.FC<PanditProps> = ({
  show_label,
  label,
  placeholder = "Search...",
  selectedValue = null,
  onChange,
  is_required,
  error,
}) => {
  const axiosInstance = useAxios();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<User[]>([]);
  const [selectedoption, setSelectedOptions] = useState<User>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const abortControllerRef = useRef<AbortController | null>(null);
  
  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      setErrorMessage("");

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response: any = await axiosInstance({
          url: "users/pandit-dropdown-list",
          method: "GET",
          params: { search_string: searchTerm },
          signal: controller.signal,
        });

        if (!response.error) {
          setOptions(response.data.users);
        } else {
          setOptions([]);
          setErrorMessage("Unexpected response format");
        }
      } catch (error: any) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          setErrorMessage("Failed to fetch options. Please try again.");
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchOptions();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [searchTerm, isOpen, axiosInstance]);

  const handleSelect = (option: User) => {
	//console.log('option', option);  
	onChange(option);
    setSelectedOptions(option);
    setIsOpen(false);
  };

  return (
    <div className="position-relative w-100">

      {show_label && label && (
        <label className="form-label">
          {label} {is_required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* ===== Trigger ===== */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="form-select d-flex justify-content-between align-items-center"
      >
        <span className={selectedoption ? "" : "text-muted"}>
          {selectedoption ? selectedoption.fullname : "Select Pandit"}
        </span>
      </button>

      {/* ===== Dropdown ===== */}
      {isOpen && (
        <div className="dropdown-menu show w-100 mt-1 shadow">

          {/* Search */}
          <div className="px-3 py-2 border-bottom">
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-4 position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <input
                type="text"
                className="form-control ps-10"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Error */}
          {errorMessage && (
            <div className="px-3 py-2 text-danger small border-bottom bg-light-danger">
              {errorMessage}
            </div>
          )}

          {/* Options */}
          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {loading ? (
              <div className="px-3 py-2 text-muted">Loading...</div>
            ) : options.length === 0 && !errorMessage ? (
              <div className="px-3 py-2 text-muted">No results found</div>
            ) : (
              options.map((option) => (
                <button
                  key={option._id}
                  type="button"
                  className="dropdown-item d-flex align-items-center gap-3 py-2"
                  onClick={() => handleSelect(option)}
                >
                  <img
                    src={option.imagepath}
                    alt={option.fullname}
                    className="rounded border"
                    width={40}
                    height={40}
                  />
                  <span className="fw-semibold">{option.fullname}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Validation Error */}
      {error && (
        <div className="text-danger small mt-1">{error}</div>
      )}
    </div>
  );
};

export default CommonPanditSelect;
